// frontend/src/utils/conversation/buildPronounResolutionSignal.js

function normalizeText(value = '') {
  return String(value)
    .toLowerCase()
    .trim()
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/\s+/g, ' ')
    .replace(/[^\w\s']/g, '');
}

function clampScore(value = 0) {
  return Math.max(0, Math.min(1, Number(value.toFixed(2))));
}

function getRole(value = '') {
  return normalizeText(value);
}

function getEntityName(entity = null) {
  return entity?.name || entity?.firstName || entity?.first_name || null;
}

function getEntityRole(entity = null) {
  return (
    entity?.role ||
    entity?.relationship ||
    entity?.relationship_type ||
    null
  );
}

function detectPronouns(message = '') {
  const lower = normalizeText(message);

  const found = {
    she: /\bshe\b/.test(lower),
    her: /\bher\b/.test(lower),
    he: /\bhe\b/.test(lower),
    him: /\bhim\b/.test(lower),
    we: /\bwe\b/.test(lower),
    they: /\bthey\b/.test(lower),
    them: /\bthem\b/.test(lower),
  };

  const present = Object.entries(found)
    .filter(([, isPresent]) => isPresent)
    .map(([key]) => key);

  return {
    found,
    present,
    hasPronoun: present.length > 0,
  };
}

function isFemaleOrNeutralCompatible(role = '') {
  const normalizedRole = getRole(role);

  const compatibleRoles = new Set([
    'mother','mom','daughter','sister','grandmother','grandma',
    'aunt','wife','girlfriend','fiancee','friend','cousin','child',
    'children','parent','relative'
  ]);

  return compatibleRoles.has(normalizedRole);
}

function isMaleOrNeutralCompatible(role = '') {
  const normalizedRole = getRole(role);

  const compatibleRoles = new Set([
    'father','dad','son','brother','grandfather','grandpa',
    'uncle','husband','boyfriend','fiance','friend','cousin','child',
    'children','parent','relative'
  ]);

  return compatibleRoles.has(normalizedRole);
}

function hasExplicitSubjectShift(message = '') {
  const lower = normalizeText(message);

  return [
    'not ',
    'no ',
    'actually',
    'instead',
    'we are talking about',
    "we're talking about",
  ].some((phrase) => lower.includes(phrase));
}

function hasContinuationLanguage(message = '') {
  const lower = normalizeText(message);

  return [
    'we spoke',
    'we talked',
    'we went',
    'we were',
    'earlier',
    'today',
    'yesterday',
  ].some((phrase) => lower.includes(phrase));
}

/**
 * 🔥 NEW: choose best entity from entityState
 */
function selectPronounCandidate(entityState = null) {
  if (!entityState) return null;

  // 1. active always wins first
  if (entityState.active) return entityState.active;

  // 2. fallback to previous
  if (entityState.previous) return entityState.previous;

  // 3. fallback to strongest named
  if (Array.isArray(entityState.named) && entityState.named.length > 0) {
    return entityState.named[0];
  }

  return null;
}

function determineCompatibility({ pronouns = [], candidateRole = '' }) {
  if (!pronouns.length || !candidateRole) {
    return { compatible: false, compatibilityType: 'none', blockedByRole: false };
  }

  const hasFemale = pronouns.some((p) => p === 'she' || p === 'her');
  const hasMale = pronouns.some((p) => p === 'he' || p === 'him');
  const hasGroup = pronouns.some((p) => ['we','they','them'].includes(p));

  if (hasGroup) {
    return { compatible: true, compatibilityType: 'group', blockedByRole: false };
  }

  if (hasFemale) {
    const ok = isFemaleOrNeutralCompatible(candidateRole);
    return { compatible: ok, compatibilityType: ok ? 'female_or_neutral' : 'blocked', blockedByRole: !ok };
  }

  if (hasMale) {
    const ok = isMaleOrNeutralCompatible(candidateRole);
    return { compatible: ok, compatibilityType: ok ? 'male_or_neutral' : 'blocked', blockedByRole: !ok };
  }

  return { compatible: false, compatibilityType: 'none', blockedByRole: false };
}

function computeScore({
  hasPronoun,
  candidate,
  compatible,
  continuationLanguage,
  explicitSubjectShift,
}) {
  if (!hasPronoun || !candidate || explicitSubjectShift || !compatible) return 0;

  let score = 0.4;

  if (candidate?.roles?.includes('active')) score += 0.25;
  if (candidate?.roles?.includes('previous')) score += 0.1;
  if (continuationLanguage) score += 0.1;

  return clampScore(score);
}

function buildPronounResolutionSignal({
  message = '',
  activeIdentity = null,
  entityState = null,
} = {}) {
  const normalized = normalizeText(message);
  const pronounState = detectPronouns(normalized);

  const candidate = selectPronounCandidate(entityState);

  const candidateName = getEntityName(candidate);
  const candidateRole = getEntityRole(candidate);

  const explicitSubjectShift = hasExplicitSubjectShift(normalized);
  const continuationLanguage = hasContinuationLanguage(normalized);

  const compatibility = determineCompatibility({
    pronouns: pronounState.present,
    candidateRole,
  });

  const score = computeScore({
    hasPronoun: pronounState.hasPronoun,
    candidate,
    compatible: compatibility.compatible,
    continuationLanguage,
    explicitSubjectShift,
  });

  const resolved = score >= 0.7 && !!candidate;

  return {
    detected: pronounState.hasPronoun,
    pronouns: pronounState.present,
    candidateEntity: candidate,
    candidateName,
    candidateRole,
    compatibility: compatibility.compatibilityType,
    compatible: compatibility.compatible,
    blockedByRole: compatibility.blockedByRole,
    continuationLanguage,
    explicitSubjectShift,
    score,
    resolved,
    mode: resolved ? 'entity_binding' : 'defer',
    shouldBypassFallback: resolved,
    shouldCarryForwardEntity: resolved,
    reason: resolved
      ? `resolved to ${candidateName}`
      : 'no strong pronoun resolution',
  };
}

export { buildPronounResolutionSignal };